<template>
	<ContextMenu
		ref="contextMenu"
		class="ai-input-menu"
		align="top-right"
		:arrow="false"
		:keep-open="false"
		:title="disabled ? disabledReason : null"
		@change="isOpen = $event"
		@mouseenter.native="open"
	>
		<template #toggle>
			<button
				ref="button"
				class="ai-input-menu__button"
				type="button"
				:aria-expanded="isOpen ? 'true' : 'false'"
				:aria-label="buttonAriaLabel"
				:disabled="disabled || busy"
				:title="disabled ? disabledReason : null"
				aria-haspopup="menu"
				@click.stop="open"
				@keydown.esc.stop="close"
			>
				<AiSparkle
					:busy="busy"
					size="md"
				/>
			</button>
		</template>
		<template #context-menu>
			<div
				class="ai-input-menu__items"
				role="menu"
			>
				<button
					v-for="action in actions"
					:key="action.id"
					class="ai-input-menu__item"
					type="button"
					role="menuitem"
					:disabled="action.disabled || busy"
					:title="action.title || null"
					:tabindex="isOpen ? 0 : -1"
					@click.stop="selectAction(action)"
					@keydown.esc.stop="close(true)"
				>
					<V2Icon
						v-if="action.icon"
						class="ai-input-menu__icon"
						:icon-name="action.icon"
						size="sm"
					/>
					<span>{{ action.label }}</span>
				</button>
			</div>
		</template>
	</ContextMenu>
</template>

<script>
import AiSparkle from '@shared/components/AiSparkle';
import ContextMenu from '@shared/components/ContextMenu';
import V2Icon from '@shared/components/Icon';

export default {
	name:       'AiInputMenu',
	components: { AiSparkle, ContextMenu, V2Icon },
	props:      {
		actions: {
			type:    Array,
			default: () => [],
		},
		busy: {
			type:    Boolean,
			default: false,
		},
		label: {
			type:    String,
			default: 'AI',
		},
		busyLabel: {
			type:    String,
			default: null,
		},
		disabled: {
			type:    Boolean,
			default: false,
		},
		disabledReason: {
			type:    String,
			default: null,
		},
	},
	data() {
		return {
			isOpen: false,
		};
	},
	computed: {
		buttonAriaLabel() {
			return this.busy ? (this.busyLabel || this.label) : this.label;
		},
	},
	methods: {
		/** Open idempotently so hover and click share the trigger without immediately toggling it closed. */
		open() {
			if (this.disabled || this.busy || this.isOpen) {
				return;
			}

			this.$refs.contextMenu.toggleContextMenu();
		},
		close(restoreFocus = false) {
			this.$refs.contextMenu?.close();
			if (restoreFocus) {
				this.$nextTick(() => this.$refs.button?.focus());
			}
		},
		selectAction(action) {
			if (this.disabled || action.disabled || this.busy) {
				return;
			}

			this.close();
			this.$emit('select', action);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.ai-input-menu__button {
	display: flex;
	align-items: center;
	justify-content: center;
	border: 0;
	border-radius: 50%;
	padding: $sp1;
	color: var(--color-ai);
	background-color: transparent;
	cursor: pointer;
	transition: color $trans-time-fast, background-color $trans-time-fast, transform $trans-time-fast;

	&:hover,
	&:focus-visible,
	&[aria-expanded='true'] {
		color: var(--color-ai);
		background-color: rgba(var(--color-rgb-ai), 0.1);
	}

	&:focus-visible {
		outline: 2px solid var(--color-ai);
		outline-offset: 2px;
	}

	&:active:not(:disabled) {
		transform: scale(0.92);
	}

	&:disabled {
		cursor: default;
	}
}

.ai-input-menu__items {
	display: flex;
	flex-direction: column;
	padding: $sp2;
	min-width: 230px;
}

.ai-input-menu__item {
	@include font(primary, book, normal, fs-80);
	display: flex;
	align-items: center;
	border: 0;
	border-radius: var(--t-border-radius--sm);
	padding: $sp3 $sp4;
	width: 100%;
	color: var(--color-shadow-grey);
	text-align: left;
	white-space: nowrap;
	background-color: var(--color-white);
	cursor: pointer;
	transition: color $trans-time-fast, background-color $trans-time-fast;

	&:hover:not(:disabled),
	&:focus-visible {
		color: var(--color-ai);
		background-color: rgba(var(--color-rgb-ai), 0.08);
	}

	&:focus-visible {
		outline: 2px solid var(--color-ai);
		outline-offset: -2px;
	}

	&:disabled {
		color: var(--color-grey-400);
		cursor: not-allowed;
	}
}

.ai-input-menu__icon {
	margin-right: $sp3;
}
</style>
